import bucketApi from '@/api/bucketApi';
import userApi from '@/api/userApi';
import ErrorAlert from '@/components/errorAlert';
import Money from '@/components/money';
import { PageTitle } from '@/components/pageTitle';
import { ReceiptTypeCell } from '@/components/receiptTypeCell';
import { UserCell } from '@/components/userCell';
import { WorkflowStateCell } from '@/components/workflowStateCell';
import useSearchParam from '@/hooks/useSearchParams';
import { ReportButtons } from '@/pages/tableView/components/reportButtons';
import { TableActions } from '@/pages/tableView/components/tableActions';
import { TableSettings } from '@/pages/tableView/components/tableSettings';
import { bucketTableScope } from '@/pages/tableView/state/tableViewState';
import { Box } from '@mantine/core';
import { IconExternalLink } from '@tabler/icons-react';
import { createFileRoute, Link, Outlet } from '@tanstack/react-router';
import { getExpensesTotal } from 'common/helpers/expensesHelpers';
import { getReceiptTotal } from 'common/helpers/receiptHelpers';
import { DateHelper } from 'common/misc/dateHelpers';
import { filterTruthy } from 'common/misc/filterTruthy';
import { Bucket, Trip } from 'common/types/bucket';
import type { Expenses } from 'common/types/expenses';
import { Receipt } from 'common/types/receipt';
import { ReceiptType } from 'common/types/receiptType';
import { WorkflowStateValues } from 'common/types/workflow';
import { orderBy, sum } from 'lodash';
import { useMemo } from 'react';
import { DateFilter, type DateRange, SelectFilter, Table, TextFilter } from 'schummar-table';
import { useTranslator } from 'translations';
import z from 'zod';

export const Route = createFileRoute('/_auth/_home/report')({
    component: RouteComponent,
    validateSearch: z.object({
        regenerate: z.boolean().optional(),
    }),
});

function RouteComponent() {
    const t = useTranslator();
    const [allUsers] = userApi.useList();
    const usersLookup = useMemo(
        () => new Map(allUsers?.map((user) => [user.id, user])),
        [allUsers],
    );
    const managerIds = useMemo(
        () => [...new Set(allUsers?.map((user) => user.managerId).filter(filterTruthy))],
        [allUsers],
    );
    const receiptTypes = useMemo(
        () => [
            'expenses',
            ...orderBy(ReceiptType.options, (type) =>
                t.unknown(`receiptType.${type}`, {}, { fallback: type ?? '' }),
            ),
        ],
        [t],
    );

    const store = bucketTableScope.useScope();
    const [id, setId] = useSearchParam({ from: '/_auth/_home/report', key: 'id' });
    const [receiptType, setReceiptType] = useSearchParam({
        from: '/_auth/_home/report',
        key: 'receiptType',
    });
    const [locationFrom, setLocationFrom] = useSearchParam({
        from: '/_auth/_home/report',
        key: 'locationFrom',
    });
    const [locationTo, setLocationTo] = useSearchParam({
        from: '/_auth/_home/report',
        key: 'locationTo',
    });
    const [date, setDate] = useSearchParam({ from: '/_auth/_home/report', key: 'date' });
    const [workflowState, setWorkflowState] = useSearchParam({
        from: '/_auth/_home/report',
        key: 'workflowState',
    });
    const [ownerId, setOwnerId] = useSearchParam({ from: '/_auth/_home/report', key: 'ownerId' });
    const [managerId, setManagerId] = useSearchParam({
        from: '/_auth/_home/report',
        key: 'managerId',
    });

    const search = Route.useSearch();
    const [bucketsByPerson, error] = bucketApi.byPerson(search).useCache();
    const buckets = Object.values(bucketsByPerson ?? {}).flat();

    const items = useMemo(
        () =>
            buckets
                .flatMap<{
                    id: string;
                    bucket: Bucket;
                    expenses?: Expenses[];
                    trip?: Trip;
                    receipt?: Receipt;
                    parentId?: string;
                    date?: Date | { min: Date; max: Date };
                    managerId?: string;
                    total: number;
                    payableTotal?: number;
                    paidTotal?: number;
                    open?: number;
                }>((bucket) => {
                    if (bucket.type === 'trip') {
                        return [
                            {
                                id: bucket.id,
                                bucket,
                                trip: bucket,
                                total: bucket.payments?.total ?? 0,
                                payableTotal: bucket.payments?.payableTotal ?? 0,
                                paidTotal: bucket.payments?.paidTotal ?? 0,
                                open:
                                    (bucket.payments?.payableTotal ?? 0) -
                                    (bucket.payments?.paidTotal ?? 0),
                            },
                            {
                                id: `${bucket.id}_expenses`,
                                parentId: bucket.id,
                                bucket,
                                expenses: bucket.expenses,
                                total: getExpensesTotal(bucket),
                            },
                            ...bucket.receipts.map((receipt) => ({
                                id: receipt.id,
                                parentId: bucket.id,
                                bucket,
                                receipt,
                                total: getReceiptTotal(receipt),
                            })),
                        ];
                    }

                    return [
                        {
                            id: bucket.receipts[0].id,
                            bucket,
                            receipt: bucket.receipts[0],
                            total: bucket.payments?.total ?? 0,
                            payableTotal: bucket.payments?.payableTotal ?? 0,
                            paidTotal: bucket.payments?.paidTotal ?? 0,
                            open:
                                (bucket.payments?.payableTotal ?? 0) -
                                (bucket.payments?.paidTotal ?? 0),
                        },
                    ];
                })
                .map((item) => ({
                    ...item,
                    date:
                        (item.trip || item.expenses) &&
                        item.bucket.type === 'trip' &&
                        item.bucket.tripData.startedOn &&
                        item.bucket.tripData.endedOn
                            ? {
                                  min: item.bucket.tripData.startedOn,
                                  max: item.bucket.tripData.endedOn,
                              }
                            : item.receipt?.date
                              ? item.receipt.date
                              : undefined,
                    managerId: usersLookup.get(item.bucket.ownerId)?.managerId,
                })),
        [buckets, usersLookup],
    );

    return (
        <PageTitle title={t('header.tableView')}>
            <Box
                css={{
                    flex: '1 0 0',
                    overflowX: 'auto',
                    overflowY: 'scroll',
                }}
            >
                <Box
                    css={{
                        padding: 'var(--mantine-spacing-md)',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 'var(--mantine-spacing-md)',
                    }}
                >
                    <TableActions buckets={buckets} />
                    <ReportButtons buckets={buckets} />
                    <TableSettings />
                </Box>

                <Table
                    items={items}
                    id="id"
                    parentId="parentId"
                    onSelectionChange={(selection) =>
                        store.update((state) => {
                            state.selection = selection;
                        })
                    }
                    onReset={() => {
                        setId(undefined);
                        setReceiptType(undefined);
                        setLocationFrom(undefined);
                        setLocationTo(undefined);
                        setDate(undefined);
                        setWorkflowState(undefined);
                        setOwnerId(undefined);
                        setManagerId(undefined);
                    }}
                    fullWidth="left"
                    persist={{
                        id: 'bucketTable',
                        storage: localStorage,
                        exclude: ['filterValues'],
                    }}
                    defaultSort={[{ columnId: 'endedOn', direction: 'desc' }]}
                    defaultHiddenColumns={
                        new Set(['locationFrom', 'updatedOn', 'manager', 'open', 'paid'])
                    }
                    virtual
                    // enableExport={allUsers ? { copy: { all: true }, download: { all: true } } : undefined}
                    rowAction={({ bucket, receipt }) => (
                        <Box css={{ flex: 1, display: 'flex', justifyContent: 'end' }}>
                            <Link
                                to={receipt ? '/r/$receiptId' : '/t/$tripId'}
                                params={receipt ? { receiptId: receipt.id } : { tripId: bucket.id }}
                            >
                                <IconExternalLink fontSize="inherit" />
                            </Link>
                        </Box>
                    )}
                    styles={{
                        cell: (item) => ({
                            backgroundColor: item.parentId ? 'rgba(0, 0, 0, 0.1)' : undefined,

                            'div:hover > &': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                        }),
                    }}
                    defaultColumnProps={{
                        width: 'minmax(100px, 200px)',
                        exportCell: (value) =>
                            typeof value === 'string'
                                ? value
                                : value === undefined || value === null
                                  ? '-'
                                  : JSON.stringify(value),
                    }}
                    columns={(col) => [
                        col((item) => item.receipt?.id ?? item.bucket.id, {
                            id: 'id',
                            header: t('report.headers.id'),
                            filter: (
                                <SelectFilter
                                    external
                                    value={new Set(id)}
                                    onChange={(value) => setId(value && Array.from(value))}
                                />
                            ),
                            sortBy: [Number],
                        }),

                        col(
                            (item) =>
                                item.expenses ? 'expenses' : (item.receipt?.type ?? undefined),
                            {
                                id: 'type',
                                header: t('report.headers.type'),
                                renderCell: (type) => <ReceiptTypeCell type={type} />,
                                filter: (
                                    <SelectFilter
                                        external
                                        value={new Set(receiptType)}
                                        onChange={(value) =>
                                            setReceiptType(value && Array.from(value))
                                        }
                                        options={receiptTypes}
                                        stringValue={(type: string) =>
                                            t.unknown(
                                                `receiptType.${type}`,
                                                {},
                                                { fallback: type ?? '' },
                                            ) as string
                                        }
                                        render={(type: string) => <ReceiptTypeCell type={type} />}
                                    />
                                ),
                                sortBy: [
                                    (type) =>
                                        t.unknown(
                                            `receiptType.${type}`,
                                            {},
                                            { fallback: type ?? '' },
                                        ),
                                ],
                            },
                        ),

                        col((item) => item.trip?.tripData.locationFrom?.name, {
                            id: 'locationFrom',
                            header: t('report.headers.locationFrom'),
                            filter: (
                                <TextFilter
                                    external
                                    value={locationFrom ?? ''}
                                    onChange={setLocationFrom}
                                />
                            ),
                            width: 'minmax(100px, 400px)',
                        }),

                        col((item) => item.trip?.tripData.locationTo?.name, {
                            id: 'locationTo',
                            header: t('report.headers.locationTo'),
                            filter: (
                                <TextFilter
                                    external
                                    value={locationTo ?? ''}
                                    onChange={setLocationTo}
                                />
                            ),
                            width: 'minmax(100px, 400px)',
                        }),

                        col((item) => item.date, {
                            id: 'date',
                            header: t('report.headers.date'),
                            renderCell: (date) =>
                                typeof date === 'string' ? (
                                    t.dateTimeFormat(date, { dateStyle: 'medium' })
                                ) : date ? (
                                    <>
                                        {t.dateTimeFormat(date.min, { dateStyle: 'medium' })} -{' '}
                                        {t.dateTimeFormat(date.max, { dateStyle: 'medium' })}
                                    </>
                                ) : (
                                    '-'
                                ),
                            sortBy: [(date) => (typeof date === 'string' ? date : date?.min)],
                            filter: (
                                <DateFilter
                                    external
                                    value={date && DateHelper.plainDateRangeToDateRange(date)}
                                    onChange={(value) =>
                                        setDate(
                                            value
                                                ? DateHelper.dateRangeToPlainDateRange(
                                                      value as DateRange,
                                                  )
                                                : undefined,
                                        )
                                    }
                                />
                            ),
                            exportCell: (date) =>
                                typeof date === 'string'
                                    ? date
                                    : date
                                      ? `${date.min} - ${date.max}`
                                      : '-',
                        }),

                        col((item) => item.receipt?.modifiedOn ?? item.bucket.modifiedOn, {
                            id: 'updatedOn',
                            header: t('report.headers.updatedOn'),
                            renderCell: (modifiedOn) =>
                                modifiedOn && t.dateTimeFormat(modifiedOn, { dateStyle: 'medium' }),
                        }),

                        col((item) => (!item.parentId ? item.bucket.state : undefined), {
                            id: 'state',
                            header: t('report.headers.state'),
                            renderCell: (state) => <WorkflowStateCell state={state} />,
                            filter: (
                                <SelectFilter
                                    external
                                    value={new Set(workflowState)}
                                    onChange={(value) =>
                                        setWorkflowState(value && Array.from(value))
                                    }
                                    options={WorkflowStateValues}
                                    render={(state) => <WorkflowStateCell state={state} />}
                                />
                            ),
                            sortBy: [(state) => (!state ? -1 : WorkflowStateValues.indexOf(state))],
                        }),

                        col((item) => item.bucket.ownerId, {
                            id: 'employee',
                            header: t('report.headers.employee'),
                            renderCell: (userId) => <UserCell query={userId} />,
                            filter: (
                                <SelectFilter
                                    external
                                    value={new Set(ownerId)}
                                    onChange={(value) => setOwnerId(value && Array.from(value))}
                                    options={[...usersLookup.keys()]}
                                    stringValue={(userId) =>
                                        usersLookup.get(userId)?.name ?? userId
                                    }
                                    render={(userId: string) => <UserCell query={userId} />}
                                />
                            ),
                            exportCell: (userId) => usersLookup.get(userId)?.name ?? userId,
                        }),

                        col((item) => item.managerId, {
                            id: 'manager',
                            header: t('report.headers.manager'),
                            renderCell: (managerId) =>
                                managerId ? <UserCell query={managerId} /> : '-',
                            filter: (
                                <SelectFilter
                                    external
                                    value={new Set(managerId)}
                                    onChange={(value) => setManagerId(value && Array.from(value))}
                                    options={managerIds}
                                    stringValue={(managerId) =>
                                        usersLookup.get(managerId)?.name ?? managerId
                                    }
                                    render={(managerId) => <UserCell query={managerId} />}
                                />
                            ),
                            exportCell: (managerId) =>
                                managerId ? (usersLookup.get(managerId)?.name ?? managerId) : '-',
                        }),

                        col((item) => item.total, {
                            id: 'total',
                            header: t('report.headers.total'),
                            renderCell: (sum) => <Money amount={sum} css={{ flex: 1 }} />,
                            footer: (
                                <Money
                                    amount={sum(
                                        items
                                            ?.filter((item) => !item.parentId)
                                            .map((item) => item.bucket.payments?.total ?? 0),
                                    )}
                                    css={{ flex: 1 }}
                                />
                            ),
                        }),

                        col((item) => item.payableTotal, {
                            id: 'payableTotal',
                            header: t('report.headers.payableTotal'),
                            renderCell: (sum) =>
                                sum === undefined ? (
                                    <span css={{ flex: 1, textAlign: 'right' }}>-</span>
                                ) : (
                                    <Money amount={sum} css={{ flex: 1 }} />
                                ),
                            footer: (
                                <Money
                                    amount={sum(
                                        items
                                            ?.filter((item) => !item.parentId)
                                            .map((item) => item.bucket.payments?.payableTotal ?? 0),
                                    )}
                                    css={{ flex: 1 }}
                                />
                            ),
                        }),

                        col((item) => item.paidTotal, {
                            id: 'paid',
                            header: t('report.headers.paid'),
                            renderCell: (sum) =>
                                sum === undefined ? (
                                    <span css={{ flex: 1, textAlign: 'right' }}>-</span>
                                ) : (
                                    <Money amount={sum} css={{ flex: 1 }} />
                                ),
                            footer: (
                                <Money
                                    amount={sum(
                                        items
                                            ?.filter((item) => !item.parentId)
                                            .map((item) => item.bucket.payments?.paidTotal ?? 0),
                                    )}
                                    css={{ flex: 1 }}
                                />
                            ),
                        }),

                        col((item) => item.open, {
                            id: 'open',
                            header: t('report.headers.open'),
                            renderCell: (sum) =>
                                sum === undefined ? (
                                    <span css={{ flex: 1, textAlign: 'right' }}>-</span>
                                ) : (
                                    <Money amount={sum} css={{ flex: 1 }} />
                                ),
                            footer: (
                                <Money
                                    amount={sum(
                                        items
                                            ?.filter((item) => !item.parentId)
                                            .map(
                                                (item) =>
                                                    (item.bucket.payments?.payableTotal ?? 0) -
                                                    (item.bucket.payments?.paidTotal ?? 0),
                                            ),
                                    )}
                                    css={{ flex: 1 }}
                                />
                            ),
                        }),
                    ]}
                />

                <Box
                    css={{
                        marginLeft: 'var(--mantine-spacing-xl)',
                        marginRight: 'var(--mantine-spacing-xl)',
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    {error ? (
                        <ErrorAlert error={error} />
                    ) : !bucketsByPerson ? (
                        t('general.loading')
                    ) : null}
                </Box>

                <Outlet />
            </Box>
        </PageTitle>
    );
}
